<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import SubMenuElement from './SubMenuElement.vue';
import { type Props, type Emit, setup } from './menuElement.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  innerRef,
  innerHeight,
  hasMenuContent,
  isAvailableMenu,
  mainPanelInfo,
  computedDashboardMenu,
  computedAnalysisBoardMenu,
  onMouseDown,
  onClickButton,
  onHoverButton,
} = setup(props, emit);
</script>

<template>
  <li
    :class="[
      'menu',
      'menu-element',
      `menu-element__${menuInfo.value.toLowerCase()}`,
      { selected },
      { 'no-menu': !isAvailableMenu },
    ]"
    :data-menu-value="menuInfo.value"
  >
    <base-button
      tag="button"
      type="button"
      :class="[
        'menu__title',
        'menu-element__title',
        { 'no-menu__title': !isAvailableMenu },
        `menu__${menuInfo.value.toLowerCase()}`,
      ]"
      :data-menu="menuInfo.value"
      @mousedown="onMouseDown"
      @click="onClickButton"
      @mouseenter="onHoverButton"
    >
      <div class="menu-element__title-inner menu__title-inner">
        <div
          :class="['menu-element__item-icon', `menu__item-icon--${menuInfo.value.toLowerCase()}`]"
        />
        <h3>{{ menuInfo.text }}</h3>
        <div
          v-if="hasMenuContent"
          :class="['menu-element__arrow-icon', { up: selected, down: !selected }]"
        />
      </div>
    </base-button>
    <div :class="['folding-sub-menu', { opened: selected && mainPanelInfo.isMainPanelExpanded }]">
      <ul
        ref="innerRef"
        class="folding-sub-menu__inner"
      >
        <!-- Dashboard -->
        <sub-menu-element
          v-if="menuInfo.dashboardList"
          :menu-info="computedDashboardMenu!"
          :selected="mainPanelInfo.selectedSubMenu === menuInfo.dashboardList.value"
        />

        <!-- Analysis Board -->
        <sub-menu-element
          v-if="
            menuInfo.analysisBoardList &&
            props.analysisBoardMenuList &&
            props.analysisBoardMenuList.length > 0
          "
          :menu-info="computedAnalysisBoardMenu!"
          :selected="mainPanelInfo.selectedSubMenu === menuInfo.analysisBoardList.value"
        />

        <!-- children -->
        <template v-for="(menu, menuIdx) in menuInfo?.children">
          <template v-if="!menu?.children?.length && menu.text">
            <sub-menu-element
              :key="`${menu.value}_${menuIdx}`"
              :menu-info="menu"
              :selected="mainPanelInfo.selectedSubMenu === menu.value"
            />
          </template>

          <template v-else-if="menu.children?.length">
            <li
              :key="`${menu.value}_${menuIdx}`"
              class="mid-menu"
            >
              <div class="mid-menu__title">
                <span v-show="menu.text">{{ menu.text }}</span>
              </div>
              <ul>
                <sub-menu-element
                  v-for="(middleMenuChild, middleMenuChildIndex) in menu.children"
                  :key="`${middleMenuChild.value}_${middleMenuChildIndex}`"
                  :menu-info="middleMenuChild"
                  :selected="mainPanelInfo.selectedSubMenu === middleMenuChild.value"
                />
              </ul>
            </li>
          </template>
        </template>
      </ul>
    </div>
  </li>
</template>

<style scoped lang="scss">
.menu {
  &__title {
    box-sizing: border-box;
    flex-grow: 0;
    flex-shrink: 0;
    width: var(--gnb-menu-item-content-width);
    min-width: var(--gnb-menu-item-content-width);
    max-width: var(--gnb-menu-item-content-width);
    height: var(--gnb-menu-item-height);
    padding: 8px;
    margin: 0;

    // 축소형일 때도 padding 8px 유지, 중앙 정렬
    .base-menu:not(.expanded) & {
      justify-content: center;
      padding: 8px;
    }

    // 확장형일 때 width를 100%로 변경
    .base-menu.expanded & {
      width: 100%;
      min-width: 0;
      max-width: none;
    }

    cursor: pointer;
    user-select: none;
  }

  &__title-inner {
    padding-left: 0;
  }

  &__item-icon {
    background-size: var(--gnb-menu-icon-size) var(--gnb-menu-icon-size) !important;
  }

  &.no-menu {
    cursor: default;

    .menu__title {
      cursor: default;
    }
  }

  &__home .menu__item-icon--home {
    background: url('@/common/assets/gnb/icon-home.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__dashboard .menu__item-icon--dashboard {
    background: url('@/common/assets/gnb/icon-dashboard.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__database .menu__item-icon--database {
    background: url('@/common/assets/gnb/icon-database.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__application .menu__item-icon--application {
    background: url('@/common/assets/gnb/icon-application.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__infrastructure .menu__item-icon--infrastructure {
    background: url('@/common/assets/gnb/icon-infrastructure.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__gpu .menu__item-icon--gpu {
    background: url('@/common/assets/gnb/icon-gpu.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__cloud .menu__item-icon--cloud {
    background: url('@/common/assets/gnb/icon-cloud.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__message-queue .menu__item-icon--message-queue {
    background: url('@/common/assets/gnb/icon-message-queue.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__kubernetes .menu__item-icon--kubernetes {
    background: url('@/common/assets/gnb/icon-kubernetes.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  // &__pa .menu__item-icon--pa {
  //   background: url('@/common/assets/icon-nav-pa.svg') no-repeat center center;
  // }

  /* &__config .menu__item-icon--config {
    background: url('@/common/assets/gnb/icon-config.svg') no-repeat center center;
  } */

  &__report .menu__item-icon--report {
    background: url('@/common/assets/gnb/icon-report.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__predefined-report .menu__item-icon--predefined-report {
    background: url('@/common/assets/gnb/icon-predefined-report.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__alert .menu__item-icon--alert {
    background: url('@/common/assets/gnb/icon-alert.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__business .menu__item-icon--business {
    background: url('@/common/assets/gnb/icon-business.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__logs .menu__item-icon--logs {
    background: url('@/common/assets/gnb/icon-logs.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__aiops .menu__item-icon--aiops {
    background: url('@/common/assets/gnb/icon-aiops.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  &__oracle .menu__item-icon--oracle {
    mask: url('@/common/assets/icon-nav-oracle.svg') no-repeat center center;
  }

  &__postgresql .menu__item-icon--postgresql {
    mask: url('@/common/assets/icon-nav-postgresql.svg') no-repeat center center;
  }

  &__mysql .menu__item-icon--mysql {
    mask: url('@/common/assets/icon-nav-mysql.svg') no-repeat center center;
  }

  &__sqlserver .menu__item-icon--sqlserver {
    mask: url('@/common/assets/icon-nav-sqlserver.svg') no-repeat center center;
  }

  &__redis .menu__item-icon--redis {
    mask: url('@/common/assets/icon-nav-redis.svg') no-repeat center center;
  }

  &__mongodb .menu__item-icon--mongodb {
    mask: url('@/common/assets/icon-nav-mongodb.svg') no-repeat center center;
  }

  &__tibero .menu__item-icon--tibero {
    mask: url('@/common/assets/icon-nav-tibero.svg') no-repeat center center;
  }

  &__altibase .menu__item-icon--altibase {
    mask: url('@/common/assets/icon-nav-altibase.svg') no-repeat center center;
  }

  &__redshift .menu__item-icon--redshift {
    mask: url('@/common/assets/icon-nav-redshift.svg') no-repeat center center;
  }

  &__cubrid .menu__item-icon--cubrid {
    mask: url('@/common/assets/icon-nav-cubrid.svg') no-repeat center center;
  }

  &__clickhouse .menu__item-icon--clickhouse {
    mask: url('@/common/assets/icon-nav-clickhouse.svg') no-repeat center center;
  }

  &__singlestore .menu__item-icon--singlestore {
    mask: url('@/common/assets/icon-nav-singlestore.svg') no-repeat center center;
  }

  &__tool .menu__item-icon--tool {
    background: url('@/common/assets/gnb/icon-tool.svg') no-repeat center center /
      var(--gnb-menu-icon-size) var(--gnb-menu-icon-size);
  }

  .folding-sub-menu {
    max-height: 0;
    transition: max-height $animate-gnb;

    &.opened {
      max-height: v-bind('innerHeight');
    }
  }

  &.menu-element__divider,
  .view-mode-maxgauge &.menu-element__database {
    position: relative;

    // 26.1.21 기획 변경 - 구분선 제거
    // &::before {
    //   position: absolute;
    //   bottom: 0;
    //   left: 0;
    //   width: 100%;
    //   height: 1px;
    //   background-color: var(--gnb-menu-divider-color);
    //   content: '';
    // }

    .folding-sub-menu.opened {
      margin-bottom: 7px;
    }
  }
}
</style>
